import { z } from "zod";
import {
  AccessControlSchema,
  ArchitectureSignalSchema,
  HeadingDefectSchema,
  ISSUE_SEMANTIC_FACT_KIND,
  ISSUE_SEMANTIC_SOURCE,
  JourneyRelevanceSchema,
  OriginConditionSchema,
  WordingContextSchema,
} from "../../enums/issue-semantics";

const HeadingFactSchema = z
  .object({
    kind: z.literal(ISSUE_SEMANTIC_FACT_KIND.HEADING),
    defects: z.array(HeadingDefectSchema).min(1),
  })
  .strict();

const WordingFactSchema = z
  .object({
    kind: z.literal(ISSUE_SEMANTIC_FACT_KIND.WORDING),
    context: WordingContextSchema,
    correction: z
      .object({ incorrect: z.string().min(1), correct: z.string().min(1) })
      .strict()
      .nullable(),
    alignColumnHeader: z.boolean(),
  })
  .strict();

const AccessGateFactSchema = z
  .object({
    kind: z.literal(ISSUE_SEMANTIC_FACT_KIND.ACCESS_GATE),
    controls: z.array(AccessControlSchema).min(1),
  })
  .strict();

const JourneyFactSchema = z
  .object({
    kind: z.literal(ISSUE_SEMANTIC_FACT_KIND.JOURNEY),
    relevance: JourneyRelevanceSchema,
  })
  .strict();

const OriginConditionFactSchema = z
  .object({
    kind: z.literal(ISSUE_SEMANTIC_FACT_KIND.ORIGIN_CONDITION),
    condition: OriginConditionSchema,
  })
  .strict();

const ArchitectureFactSchema = z
  .object({
    kind: z.literal(ISSUE_SEMANTIC_FACT_KIND.ARCHITECTURE),
    signals: z.array(ArchitectureSignalSchema).min(1),
  })
  .strict();

export const IssueSemanticFactSchema = z.discriminatedUnion("kind", [
  HeadingFactSchema,
  WordingFactSchema,
  AccessGateFactSchema,
  JourneyFactSchema,
  OriginConditionFactSchema,
  ArchitectureFactSchema,
]);
export type IssueSemanticFact = z.infer<typeof IssueSemanticFactSchema>;

export const IssueSemanticFactsSchema = z
  .array(IssueSemanticFactSchema)
  .superRefine((facts, context) => {
    const kinds = new Set<string>();
    for (const [index, fact] of facts.entries()) {
      if (kinds.has(fact.kind)) {
        context.addIssue({
          code: "custom",
          path: [index, "kind"],
          message: `Semantic fact kind ${fact.kind} may appear only once.`,
        });
      }
      kinds.add(fact.kind);
    }
  });
export type IssueSemanticFacts = z.infer<typeof IssueSemanticFactsSchema>;

export const IssueSemanticProfileSchema = z.discriminatedUnion("source", [
  z
    .object({
      source: z.literal(ISSUE_SEMANTIC_SOURCE.UNCLASSIFIED),
      facts: z.tuple([]),
    })
    .strict(),
  z
    .object({
      source: z.literal(ISSUE_SEMANTIC_SOURCE.AGENT),
      facts: IssueSemanticFactsSchema,
    })
    .strict(),
]);
export type IssueSemanticProfile = z.infer<typeof IssueSemanticProfileSchema>;

export const UnclassifiedIssueSemanticProfile: IssueSemanticProfile = {
  source: ISSUE_SEMANTIC_SOURCE.UNCLASSIFIED,
  facts: [],
};
